import axiosInstance from '../axios.config';
import { API_ENDPOINTS } from '../endpoints';
import { ApiResponse, AuditLog, AuditLogFilters, AuditLogResponse } from 'types';

export const auditService = {
  /**
   * Search audit logs with filters
   */
  async searchAuditLogs(filters: AuditLogFilters): Promise<ApiResponse<AuditLogResponse>> {
    try {
      const params: Record<string, any> = {};

      if (filters.organizationId) params.organizationId = filters.organizationId;
      if (filters.actorType) params.actorType = filters.actorType;
      if (filters.actorId) params.actorId = filters.actorId;
      if (filters.actorRole) params.actorRole = filters.actorRole;
      if (filters.actionType) params.actionType = filters.actionType;
      if (filters.resourceType) params.resourceType = filters.resourceType;
      if (filters.resourceId) params.resourceId = filters.resourceId;
      if (filters.triggerSource) params.triggerSource = filters.triggerSource;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      // Convert frontend 1-based page to backend 0-based page
      params.page = filters.page !== undefined ? Math.max(0, filters.page - 1) : 0;
      params.size = filters.size || 50;

      const response = await axiosInstance.get(
        API_ENDPOINTS.AUDIT_LOGS,
        { params }
      );

      // Transform Spring's PageResponse to frontend AuditLogResponse format
      const backendData = response.data.data;
      const transformedData: AuditLogResponse = {
        logs: backendData.content || backendData.logs || [],
        currentPage: backendData.page ?? backendData.currentPage ?? 0,
        totalItems: backendData.totalElements ?? backendData.totalItems ?? 0,
        totalPages: backendData.totalPages ?? 0,
        pageSize: backendData.size ?? backendData.pageSize ?? 50,
      };

      return {
        success: response.data.success,
        data: transformedData,
        message: response.data.message,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch audit logs'
      };
    }
  },

  /**
   * Get audit logs for a specific organization
   */
  async getByOrganization(organizationId: string, page = 0, size = 50): Promise<ApiResponse<AuditLogResponse>> {
    try {
      const response = await axiosInstance.get(
        API_ENDPOINTS.AUDIT_BY_ORGANIZATION(organizationId),
        { params: { page, size } }
      );

      // Transform Spring's PageResponse to frontend AuditLogResponse format
      const backendData = response.data.data;
      const transformedData: AuditLogResponse = {
        logs: backendData.content || backendData.logs || [],
        currentPage: backendData.page ?? backendData.currentPage ?? 0,
        totalItems: backendData.totalElements ?? backendData.totalItems ?? 0,
        totalPages: backendData.totalPages ?? 0,
        pageSize: backendData.size ?? backendData.pageSize ?? 50,
      };

      return {
        success: response.data.success,
        data: transformedData,
        message: response.data.message,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch organization audit logs'
      };
    }
  },

  /**
   * Get history of a specific resource
   */
  async getResourceHistory(resourceType: string, resourceId: string): Promise<ApiResponse<AuditLog[]>> {
    try {
      const response = await axiosInstance.get<ApiResponse<AuditLog[]>>(
        API_ENDPOINTS.AUDIT_RESOURCE_HISTORY(resourceType, resourceId)
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch resource history'
      };
    }
  },

  /**
   * Get activity for a specific user
   */
  async getUserActivity(userId: string, page = 0, size = 50): Promise<ApiResponse<AuditLogResponse>> {
    try {
      const response = await axiosInstance.get(
        API_ENDPOINTS.AUDIT_USER_ACTIVITY(userId),
        { params: { page, size } }
      );

      // Transform Spring's PageResponse to frontend AuditLogResponse format
      const backendData = response.data.data;
      const transformedData: AuditLogResponse = {
        logs: backendData.content || backendData.logs || [],
        currentPage: backendData.page ?? backendData.currentPage ?? 0,
        totalItems: backendData.totalElements ?? backendData.totalItems ?? 0,
        totalPages: backendData.totalPages ?? 0,
        pageSize: backendData.size ?? backendData.pageSize ?? 50,
      };

      return {
        success: response.data.success,
        data: transformedData,
        message: response.data.message,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch user activity'
      };
    }
  },

  /**
   * Get login/logout activity
   */
  async getLoginActivity(userId?: string, page = 0, size = 50): Promise<ApiResponse<AuditLogResponse>> {
    try {
      const params: Record<string, any> = { page, size };
      if (userId) params.userId = userId;

      const response = await axiosInstance.get(
        API_ENDPOINTS.AUDIT_LOGINS,
        { params }
      );

      // Transform Spring's PageResponse to frontend AuditLogResponse format
      const backendData = response.data.data;
      const transformedData: AuditLogResponse = {
        logs: backendData.content || backendData.logs || [],
        currentPage: backendData.page ?? backendData.currentPage ?? 0,
        totalItems: backendData.totalElements ?? backendData.totalItems ?? 0,
        totalPages: backendData.totalPages ?? 0,
        pageSize: backendData.size ?? backendData.pageSize ?? 50,
      };

      return {
        success: response.data.success,
        data: transformedData,
        message: response.data.message,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch login activity'
      };
    }
  },

  /**
   * Get audit logs by action type
   */
  async getByActionType(
    actionType: string,
    organizationId?: string,
    startDate?: string,
    endDate?: string,
    page = 0,
    size = 50
  ): Promise<ApiResponse<AuditLogResponse>> {
    try {
      const params: Record<string, any> = { page, size };
      if (organizationId) params.organizationId = organizationId;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await axiosInstance.get(
        API_ENDPOINTS.AUDIT_BY_ACTION(actionType),
        { params }
      );

      // Transform Spring's PageResponse to frontend AuditLogResponse format
      const backendData = response.data.data;
      const transformedData: AuditLogResponse = {
        logs: backendData.content || backendData.logs || [],
        currentPage: backendData.page ?? backendData.currentPage ?? 0,
        totalItems: backendData.totalElements ?? backendData.totalItems ?? 0,
        totalPages: backendData.totalPages ?? 0,
        pageSize: backendData.size ?? backendData.pageSize ?? 50,
      };

      return {
        success: response.data.success,
        data: transformedData,
        message: response.data.message,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch audit logs by action'
      };
    }
  },

  /**
   * Get audit logs by trigger source
   */
  async getByTriggerSource(
    triggerSource: string,
    organizationId?: string,
    page = 0,
    size = 50
  ): Promise<ApiResponse<AuditLogResponse>> {
    try {
      const params: Record<string, any> = { page, size };
      if (organizationId) params.organizationId = organizationId;

      const response = await axiosInstance.get(
        API_ENDPOINTS.AUDIT_BY_SOURCE(triggerSource),
        { params }
      );

      // Transform Spring's PageResponse to frontend AuditLogResponse format
      const backendData = response.data.data;
      const transformedData: AuditLogResponse = {
        logs: backendData.content || backendData.logs || [],
        currentPage: backendData.page ?? backendData.currentPage ?? 0,
        totalItems: backendData.totalElements ?? backendData.totalItems ?? 0,
        totalPages: backendData.totalPages ?? 0,
        pageSize: backendData.size ?? backendData.pageSize ?? 50,
      };

      return {
        success: response.data.success,
        data: transformedData,
        message: response.data.message,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch audit logs by source'
      };
    }
  }
};
